import { SettingKey } from 'data/ppoints/constants';
import useActivateIntegrationMode from 'features/integration/hooks/useActivateIntegrationMode';
import useSettings from 'hooks/useSettings';
import { IntegrationModeSetting } from 'models/ppoints/settings/SettingsToEcom';
import { useCallback } from 'react';

/**
 * The integration-mode step comes right after the shipping-rate step. When the merchant finishes
 * the shipping-rate step with no integration mode saved yet, activate the Smart Delivery Page (the
 * main integration) as the default, so the shop works out of the box and the next step opens with
 * that choice already selected. Best effort: a failed activation shows its own banner and never blocks the shipping-rate
 * step from completing.
 */
const useEnsureDefaultIntegrationMode = () => {
    const { settings: integrationMode, fetching } = useSettings<IntegrationModeSetting>(SettingKey.integration_mode);
    const { activateIntegrationMode } = useActivateIntegrationMode();

    const ensureDefaultIntegrationMode = useCallback(async () => {
        if (fetching || integrationMode) return;

        try {
            await activateIntegrationMode(IntegrationModeSetting.smart_delivery_page);
        } catch {
            // Reported by activateIntegrationMode's own banners.
        } finally {
            // activateIntegrationMode leaves the top-bar loader on when it bails out early.
            shopify.loading(false);
        }
    }, [fetching, integrationMode, activateIntegrationMode]);

    return { ensureDefaultIntegrationMode };
};

export default useEnsureDefaultIntegrationMode;
